import React, { useEffect, useState } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { Button, Modal, Progress, message } from 'antd'
import { remoteImgToBase64, timeout } from '../../../utils/common'
import moment from 'moment'
import axios from 'axios'
import { formDataRequest } from '../../../api/axios'
import { isMarketingSiteEnabled } from '../../../utils/constant'

export const RenderImageUrl = ({ url }) => {
  const [data, setData] = useState()
  useEffect(() => {
    if (!url) return
    remoteImgToBase64(url).then((e) => {
      setData(e)
    })
  }, [url])
  return (
    <>
      {' '}
      {data && (
        <img
          style={{ borderRadius: 8, width: 212, height: 220 }}
          src={data}
          alt=""
        />
      )}{' '}
    </>
  )
}

const GenericPdfDownloader = ({
  ids,
  filename,
  person,
  className,
  uploadRequest,
  isPublished,
  reportId
}) => {
  const [disabled, setDisabled] = useState(false)
  const [percent, setPercent] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  // branded (marketing-site) dark banner styling for the banner action buttons
  const branded = isMarketingSiteEnabled(person)
  const brandedButtonStyle = branded
    ? {
        background: 'transparent',
        border: '1px solid #c5beb3',
        color: '#c5beb3'
      }
    : {}

  useEffect(() => {
    if (uploadRequest) {
      setIsModalOpen(true)
      uploadPDF()
        .then(() => {
          setIsModalOpen(false)
          message.destroy()
          message.success('Upload successful!')
          setTimeout(() => {
            window.close()
          }, 500)
        })
        .catch(() => {
          setIsModalOpen(false)
          message.destroy()
          message.error('Fail to upload!')
        })
    }
  }, [uploadRequest])

  const uploadPDF = async () => {
    message.open({
      type: 'loading',
      content: 'PDF is creating...',
      duration: 0
    })
    await timeout(400)
    const pdf = await generatorPDF()
    message.destroy()
    message.open({
      type: 'loading',
      content: 'PDF is uploading...',
      duration: 0
    })
    const title =
      'Assessment Review' +
      moment().format('MM/DD/YYYY') +
      person.first_name.substr(0, 2).toUpperCase() +
      person.last_name.substr(0, 2).toUpperCase() +
      String(person.id).padStart(4, '0')
    const file = pdf.output('blob')
    const path = `/files/ar_deck?person_id=${
      person.id
    }&file_name=AssessmentReview_${person.id}_${new Date().getTime()}.pdf`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('size', file.size)
    formData.set('title', title)
    formData.set('size', file.size)
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()
    await formDataRequest({
      method: 'post',
      path,
      data: formData,
      onBeforeUpload: () => {
        setPercent(0)
      },
      onUploadProgress: (process) => {
        const percent = (
          (process.loaded / process.total).toFixed(2) * 100
        ).toFixed(0)
        return setPercent(percent)
      },
      cancelSource: source
    })
  }

  // html2canvas does NOT support CSS filters, so the branded view's
  // `filter: brightness(0) invert(1)` on illustrations is ignored during PDF
  // capture and the original blue-stroke SVGs show through. To fix this we
  // pre-process those images: replicate the filter by painting every opaque
  // pixel white (alpha preserved) on an offscreen canvas and swapping the src
  // for a dataURL. Originals are restored after capture.
  // Images tagged data-branded already are white-stroke branded art (see
  // utils/brandedImage.js) — whitening them again is a no-op at best, and
  // must not be re-inverted, so they are excluded from the selector.
  const BRANDED_FILTER_SELECTOR = [
    'section .metric-image-conatiner img:not([data-branded])',
    'section .balance-layout .image > img:not([data-branded])',
    'section .image-container img:not([data-branded])',
    'section .image-around img:not([data-branded])',
    'section .female-sprint-img:not([data-branded])'
  ].join(', ')

  const whitenImage = (img) => {
    if (!img.complete || !img.naturalWidth) return null
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    let imageData
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    } catch (e) {
      return null // tainted canvas (cross-origin) — skip
    }
    const px = imageData.data
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] > 0) {
        px[i] = 255
        px[i + 1] = 255
        px[i + 2] = 255
      }
    }
    ctx.putImageData(imageData, 0, 0)
    return canvas.toDataURL('image/png')
  }

  const applyBrandedImageInversion = () => {
    const swapped = []
    document.querySelectorAll(BRANDED_FILTER_SELECTOR).forEach((img) => {
      const dataUrl = whitenImage(img)
      if (dataUrl) {
        swapped.push({ img, original: img.src })
        img.src = dataUrl
      }
    })
    return swapped
  }

  const restoreBrandedImages = (swapped) => {
    swapped.forEach(({ img, original }) => {
      img.src = original
    })
  }

  const generatorPDF = async () => {
    const branded = isMarketingSiteEnabled(person)
    const swappedImages = branded ? applyBrandedImageInversion() : []
    const footer = document.getElementById('footer-preview')
    const avatar = document.getElementById('avatar')
    const saveASPDF = document.getElementById('saveASPDF')
    const copyClient = document.getElementById('copyClient')

    if (footer && footer.style) footer.style.display = 'none'
    if (saveASPDF && saveASPDF.style) saveASPDF.style.display = 'none'
    if (copyClient && copyClient.style) copyClient.style.display = 'none'
    if (avatar && avatar.style) avatar.style.opacity = '0'

    try {
      const sections = document.querySelectorAll('section')
      if (sections.length === 0) {
        console.error('No sections found in the DOM')
        return
      }

      const pdf = new jsPDF('p', 'px')
      const hideMessage = message.loading('Processing page 1...', 0)
      const firstThreeSections = Array.from(sections).slice(0, 3)
      const canvases = []
      for (const section of firstThreeSections) {
               const canvas = await html2canvas(section, {
          useCORS: true,
          imageTimeout: 60000,
          allowTaint: false,
          backgroundColor: branded ? '#0d0d0d' : '#FFFFFF',
          scale: 1
        })
        canvases.push(canvas)
      }

      const totalHeight = canvases.reduce(
        (sum, canvas) => sum + canvas.height,
        0
      )
      const combinedCanvas = document.createElement('canvas')
      combinedCanvas.width = canvases[0].width
      combinedCanvas.height = totalHeight

      const ctx = combinedCanvas.getContext('2d')
      let currentHeight = 0
      canvases.forEach((canvas) => {
        ctx.drawImage(canvas, 0, currentHeight)
        currentHeight += canvas.height
      })

      const combinedDataUrl = combinedCanvas.toDataURL('image/jpeg')
      pdf.internal.pageSize.setWidth(combinedCanvas.width)
      pdf.internal.pageSize.setHeight(totalHeight)
      pdf.addImage(
        combinedDataUrl,
        'JPEG',
        0,
        0,
        combinedCanvas.width,
        totalHeight
      )
      hideMessage()

      for (let i = 3; i < sections.length; i++) {
        const hidePageMessage = message.loading(`Processing page ${i - 1}...`, 0)
        const section = sections[i]

                const canvas = await html2canvas(section, {
          useCORS: true,
          imageTimeout: 60000,
          allowTaint: false,
          backgroundColor: branded ? '#0d0d0d' : '#FFFFFF',
          scale: 1
        })

        const canvasHeight = canvas.height
        const canvasWidth = canvas.width

        pdf.addPage()
        pdf.internal.pageSize.setWidth(canvasWidth)
        pdf.internal.pageSize.setHeight(canvasHeight)

        const sectionDataUrl = canvas.toDataURL('image/jpeg')
        pdf.addImage(sectionDataUrl, 'JPEG', 0, 0, canvasWidth, canvasHeight)

        hidePageMessage()
      }

      message.success('PDF generation completed!')

      restoreBrandedImages(swappedImages)

      if (footer && footer.style) footer.style.display = 'flex'
      if (avatar && avatar.style) avatar.style.opacity = '1'
      if (saveASPDF && saveASPDF.style) saveASPDF.style.display = 'block'
      if (copyClient && copyClient.style) copyClient.style.display = 'block'

      return pdf
    } catch (error) {
      console.error('Error generating PDF:', error)
      restoreBrandedImages(swappedImages)
      message.error('Error generating PDF. Please try again.')
    }
  }

  const downloadPdfDocument = async () => {
    setDisabled(true)
    // message.open({
    //   type: 'loading',
    //   content: 'PDF in progress..',
    //   duration: 0
    // })
    const doc = await generatorPDF()
    doc.save(filename)
    message.destroy()
    setDisabled(false)
  }

  const copyClientViewLink = () => {
    const clientViewLink = `${window.location.origin}/app/ass-report/preview/${reportId}`
    navigator.clipboard
      .writeText(clientViewLink)
      .then(() => message.success('Assessment Link Copied!'))
      .catch(() => message.error('Failed to copy Assessment link.'))
  }

  return (
    <>
      {isPublished && (
        <Button
          type="primary"
          disabled={disabled}
          className={className}
          style={{
            position: 'absolute',
            right: 150,
            top: 15,
            paddingTop: '6px',
            ...brandedButtonStyle,
            border: branded ? '1px solid #c5beb3' : '1px solid #ffffff'
          }}
          onClick={copyClientViewLink}
          id="copyClient"
        >
          COPY CLIENT LINK
        </Button>
      )}
      <Button
        type="primary"
        style={{
          paddingTop: '6px',
          ...brandedButtonStyle,
          border: branded ? '1px solid #c5beb3' : undefined
        }}
        disabled={disabled}
        className={className}
        onClick={downloadPdfDocument}
        id="saveASPDF"
      >
        SAVE AS PDF
      </Button>
      <Modal
        title="Please don't close this page until the PDF upload is complete!"
        open={isModalOpen}
        closable={false}
        footer={null}
        width={800}
      >
        <Progress percent={percent} status="active" />
      </Modal>
    </>
  )
}

export default GenericPdfDownloader
